import { useState, useEffect, useMemo } from "react";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { db } from "../utils/firebase";
import { useUser } from "../context/UserContext";
import toasted from "../config/toasted";
import { Offcanvas } from "react-bootstrap";

function Profile({ show, handleClose, message, setMessage }) {
     const { userData } = useUser();


     console.log(userData)

     const defaultProfile = useMemo(() => ({
          name: "",
          phone: "",
          city: "",
          about: "",
          website: "",
          social: {
               instagram: "",
               youtube: "",
               facebook: "",
               threads: "",
               twitter: "",
               github: ""
          }
     }), []);

     const [canUpdate, setCanUpdate] = useState(true);
     const [profile, setProfile] = useState(defaultProfile);

     useEffect(() => {
          if (!userData?.uid) {
               setCanUpdate(true);
               setMessage("");
               setProfile(defaultProfile);
               return;
          }

          const loadProfile = async () => {
               try {
                    const userRef = doc(db, "users", userData.uid);
                    const snap = await getDoc(userRef);
                    const now = Date.now();

                    if (!snap.exists()) {
                         // new account / no profile doc
                         setProfile(defaultProfile);
                         setMessage("");
                         setCanUpdate(true);
                         return;
                    }

                    const data = snap.data();

                    setProfile({
                         name: data.name || "",
                         phone: data.phone || "",
                         city: data.city || "",
                         about: data.about || "",
                         website: data.website || "",
                         social: {
                              instagram: data.social?.instagram || "",
                              youtube: data.social?.youtube || "",
                              facebook: data.social?.facebook || "",
                              threads: data.social?.threads || "",
                              twitter: data.social?.twitter || "",
                              github: data.social?.github || ""
                         }
                    });

                    const updatedAtValue = data.profileUpdatedAt;

                    if (!updatedAtValue) {
                         setMessage("");
                         setCanUpdate(true);
                         return;
                    }

                    const lastUpdate =
                         updatedAtValue?.toMillis
                              ? updatedAtValue.toMillis()
                              : updatedAtValue;

                    const diffDays = Math.floor(
                         (now - lastUpdate) / (1000 * 60 * 60 * 24)
                    );

                    if (diffDays < 7) {
                         const remaining = 7 - diffDays;
                         const msg = `Profile can be updated after ${remaining} day${remaining > 1 ? "s" : ""}`;
                         setMessage(msg);
                         setCanUpdate(false);
                    } else {
                         setMessage("");
                         setCanUpdate(true);
                    }
               } catch (error) {
                    console.error("Load profile error:", error);
                    setCanUpdate(true);
                    setMessage("");
               }
          };

          loadProfile();
     }, [userData?.uid, setMessage,defaultProfile ]);

     const handleSave = async () => {
          if (!userData?.uid) {
               toasted.error("Auth initializing... please wait");
               return;
          }

          if (!canUpdate) return;

          try {
               const userRef = doc(db, "users", userData.uid);
               const snap = await getDoc(userRef);
               const now = Date.now();

               const payload = {
                    ...profile,
                    uid: userData.uid,
                    email: userData.email || "",
                    phone: profile.phone || "", 
                    provider: userData.providerId || "",
                    emailVerified: true,
                    role: "user",
                    profileUpdatedAt: now, // better field name
                    createdAt: snap.exists() ? snap.data().createdAt || now : now
               };

               await setDoc(userRef, payload, { merge: true });

               toasted.success("Profile Updated! 🎉");
               handleClose()
               setMessage("Profile can be updated after 7 days");
               setCanUpdate(false);
          } catch (error) {
               console.error("Save profile error:", error);
               toasted.error("Failed to update profile");
          }
     };

     return (
          <Offcanvas className="profile-offcanvas" show={show} onHide={handleClose}>
               <Offcanvas.Header closeButton>
                    <Offcanvas.Title>Edit Profile</Offcanvas.Title>
               </Offcanvas.Header>

               <Offcanvas.Body>
                    <div className="edit-profile-sec">
                         <div className="row row-gap-3">
                              <div className="col-12">
                                   <div className="__p-head-profile">
                                        <p>
                                             Your basic profile details such as name and city. This information helps people identify and connect with you on the platform.
                                        </p>
                                        {!userData && <p className="text-warning">Loading user session...</p>}
                                        {message && <p className="text-danger">{message}</p>}
                                   </div>
                              </div>

                              <div className="col-12">
                                   <input
                                        placeholder="Enter Your Name"
                                        className="form-control"
                                        value={profile.name}
                                        onChange={(e) =>
                                             setProfile({ ...profile, name: e.target.value })
                                        }
                                   />
                              </div>

                              <div className="col-12">
                                   <input
                                        placeholder="Enter Your Phone Number"
                                        className="form-control"
                                        value={profile.phone}
                                        onChange={(e) =>
                                             setProfile({ ...profile, phone: e.target.value })
                                        }
                                   />
                              </div>

                              <div className="col-12">
                                   <input
                                        className="form-control"
                                        placeholder="Enter Your City"
                                        value={profile.city}
                                        onChange={(e) =>
                                             setProfile({ ...profile, city: e.target.value })
                                        }
                                   />
                              </div>

                              <div className="col-12">
                                   <input
                                        className="form-control"
                                        placeholder="Enter Your Website"
                                        value={profile.website}
                                        onChange={(e) =>
                                             setProfile({ ...profile, website: e.target.value })
                                        }
                                   />
                              </div>

                              <div className="col-12">
                                   <textarea
                                        rows={8}
                                        className="form-control"
                                        placeholder="About"
                                        value={profile.about}
                                        onChange={(e) =>
                                             setProfile({ ...profile, about: e.target.value })
                                        }
                                   />
                              </div>

                              <div className="col-12 mt-4">
                                   <h4>Social Profiles</h4>
                                   <h6>Add your public social accounts to grow your network.</h6>
                              </div>

                              {Object.keys(profile.social).map((key) => (
                                   <div className="col-12" key={key}>
                                        <input
                                             className="form-control"
                                             placeholder={`Enter Your ${key} Links`}
                                             value={profile.social[key]}
                                             onChange={(e) =>
                                                  setProfile({
                                                       ...profile,
                                                       social: {
                                                            ...profile.social,
                                                            [key]: e.target.value
                                                       }
                                                  })
                                             }
                                        />
                                   </div>
                              ))}

                              <div className="col-12">
                                   <div className="submit-btn-sec">
                                        <button
                                             disabled={!userData || !canUpdate}
                                             onClick={handleSave}
                                             className="btn"
                                        >
                                             Save Profile
                                        </button>
                                   </div>
                              </div>
                         </div>
                    </div>
               </Offcanvas.Body>
          </Offcanvas>
     );
}

export default Profile;